<script setup>
import { computed } from "vue"
import { useFilterableList } from "../composables/useFilterableList"
import { useViewMode } from "../composables/useViewMode"
import { dispatchOpenContentWindow } from "../composables/useWindowRegistry"
import { shouldOpenInContentWindow } from "../utils/content_window_link"

const props = defineProps({
  itemsByLetter: { type: Object, required: true },
  languageLinks: { type: Array, default: () => [] },
  backLink: { type: Object, default: null },
  labels: { type: Object, default: () => ({}) },
  storageKey: { type: String, default: "contentsViewMode" },
  openInWindow: { type: Boolean, default: true },
})

const label = (key, fallback) => props.labels[key] || fallback

const { query, letters, filteredGroups, showNoResults, letterHasItems, jumpToLetter } =
  useFilterableList(props.itemsByLetter)

const { viewMode, setViewMode } = useViewMode(props.storageKey)

const sortedGroups = computed(() =>
  Object.entries(filteredGroups.value).sort(([a], [b]) => a.localeCompare(b))
)

const listClass = computed(() => ({
  "contents-list": true,
  "view-list": viewMode.value === "list",
  "view-cards": viewMode.value === "cards",
}))

function openItem(item, event) {
  if (!props.openInWindow || !shouldOpenInContentWindow(event)) return
  event.preventDefault()
  event.stopPropagation()
  dispatchOpenContentWindow({
    id: item.id,
    title: item.name,
    url: item.url,
  })
}
</script>

<template>
  <div class="filterable-contents-list list-page-body">
    <aside class="list-page-sidebar" :aria-label="label('sidebarNavigation', 'Navigation')">
      <div class="list-page-sidebar-inner">
        <div v-if="backLink" class="list-sidebar-section">
          <a
            :href="backLink.url"
            class="list-sidebar-link list-sidebar-back"
            :data-turbo-frame="backLink.turboFrame || 'contents_browser'"
            :data-turbo-action="backLink.turboAction || 'advance'"
          >
            <i class="bi bi-arrow-left" />
            {{ backLink.label }}
          </a>
        </div>

        <div class="list-sidebar-section">
          <h2 class="list-sidebar-title">
            {{ label("filterTitle", "Filter") }}
          </h2>
          <div class="contents-toolbar">
            <div class="contents-toolbar-inner">
              <div class="contents-filter">
                <div class="contents-filter-wrapper">
                  <i class="bi bi-search contents-filter-icon" />
                  <input
                    v-model="query"
                    type="text"
                    :placeholder="label('filterPlaceholder', 'Filter items...')"
                    class="contents-filter-input"
                  >
                </div>
              </div>

              <div v-if="languageLinks.length" class="language-toggle">
                <a
                  v-for="link in languageLinks"
                  :key="link.code"
                  :href="link.url"
                  class="lang-btn"
                  :class="{ active: link.active }"
                  :data-turbo-frame="link.turboFrame || null"
                >{{ link.code }}</a>
              </div>

              <div class="contents-view-toggle">
                <button
                  type="button"
                  class="view-btn"
                  :class="{ active: viewMode === 'list' }"
                  data-bs-toggle="tooltip"
                  :data-bs-title="label('viewList', 'List view')"
                  @click="setViewMode('list')"
                >
                  <i class="bi bi-list" />
                </button>
                <button
                  type="button"
                  class="view-btn"
                  :class="{ active: viewMode === 'cards' }"
                  data-bs-toggle="tooltip"
                  :data-bs-title="label('viewCards', 'Card view')"
                  @click="setViewMode('cards')"
                >
                  <i class="bi bi-grid-3x3-gap" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="list-sidebar-section">
          <h2 class="list-sidebar-title">
            {{ label("alphabetTitle", "A–Z") }}
          </h2>
          <nav class="alphabet-nav alphabet-nav--vertical">
            <template v-for="letter in letters" :key="letter">
              <a
                v-if="letterHasItems(letter)"
                :href="`#letter-${letter}`"
                class="alphabet-link"
                @click="jumpToLetter($event, letter)"
              >{{ letter }}</a>
              <span v-else class="alphabet-link disabled">{{ letter }}</span>
            </template>
          </nav>
        </div>
      </div>
    </aside>

    <div class="list-page-main">
      <div :class="listClass">
        <section
          v-for="[letter, items] in sortedGroups"
          :id="`letter-${letter}`"
          :key="letter"
          class="letter-group"
          :data-letter="letter"
        >
          <h3 class="letter-heading">
            {{ letter }}
          </h3>
          <div class="items-columns">
            <div
              v-for="item in items"
              :key="item.id"
              class="content-item"
            >
              <a
                :href="item.url"
                class="item-link"
                data-turbo="false"
                :data-content-window-id="openInWindow ? item.id : null"
                :data-content-window-title="openInWindow ? item.name : null"
                @click="openItem(item, $event)"
              >
                <span class="item-name">{{ item.name }}</span>
                <i
                  v-if="item.favorited"
                  class="bi bi-star-fill favorite-indicator"
                />
              </a>
            </div>
          </div>
        </section>

        <div v-if="showNoResults" class="no-results">
          <i class="bi bi-search" />
          <p>{{ label("noResults", "No results found") }}</p>
        </div>
      </div>
    </div>
  </div>
</template>
